import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Divider, MenuItem, TextField, Typography } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import React from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useCreateIntraSquadTournament } from "../api/TournamentApiProvider";
import { useRetrievePlayersByTeamId, useRetrieveTeamPreferences, useRetrieveUserTeamPermissions, } from "../ApiProvider";
import { TeamChooser, TOURNAMENT_TEAM_COLOR_BLUE, TOURNAMENT_TEAM_COLOR_RED, TOURNAMENT_TEAM_COLOR_WHITE, } from "../matches/TeamChooser";
import { ErrorView } from "../nav/Error";
import { Forbidden } from "../nav/Forbidden";
import { Loading } from "../nav/Loading";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import { FormActions } from "../utils/FormActions";
import { TournamentType } from "./TournamentType";
const halfLengths = Array.from({ length: 9 }, (_, i) => (i + 1) * 5);
const halfTimeLengths = [
    0,
    2,
    ...Array.from({ length: 3 }, (_, i) => (i + 1) * 5),
];
const breakLengths = Array.from({ length: 12 }, (_, i) => (i + 1) * 5);
export const IntraSquadTournamentForm = ({ team, }) => {
    const navigate = useNavigate();
    // Check permissions
    const { data: userTeamPermissions, isLoading: userTeamPermissionsIsLoading, error: userTeamPermissionsError, } = useRetrieveUserTeamPermissions({ teamId: team?.id });
    const { canModify = false } = userTeamPermissions || {};
    const mutation = useCreateIntraSquadTournament({
        team,
        onMutate: () => { },
        onSuccess: (resp) => {
            navigate(`/teams/${team.id}/intra-squad-tournaments/${resp.data.id}`);
        },
        onError: () => { },
    });
    const { isLoading: teamPreferencesIsLoading, error: teamPreferencesError, data: teamPreference, } = useRetrieveTeamPreferences({ teamId: team.id });
    const { data: players, isLoading: playersLoading, isError: playersError, } = useRetrievePlayersByTeamId({ teamId: team.id });
    const [teamSelection, _setTeamSelection] = React.useState({
        teams: [],
        bench: [],
    });
    const setTeamSelection = (newTeamSelection) => {
        _setTeamSelection(newTeamSelection);
        setValue("intraSquadTournamentTeams", newTeamSelection.teams.map((team) => ({
            name: team.name,
            players: team.players.map((p) => p.id),
            primaryColour: team.primaryColour,
        })));
    };
    const useFormProps = useForm({
        defaultValues: {
            tournamentName: "",
            tournamentType: TournamentType.RoundRobin,
            startTime: undefined,
            halfLength: 45,
            halfTimeLength: 15,
            breakLength: 5,
        },
    });
    React.useEffect(() => {
        if (players) {
            setTeamSelection({
                teams: [
                    {
                        name: `${TOURNAMENT_TEAM_COLOR_RED.name} Team`,
                        players: [],
                        primaryColour: TOURNAMENT_TEAM_COLOR_RED.hex,
                    },
                    {
                        name: `${TOURNAMENT_TEAM_COLOR_BLUE.name} Team`,
                        players: [],
                        primaryColour: TOURNAMENT_TEAM_COLOR_BLUE.hex,
                    },
                    {
                        name: `${TOURNAMENT_TEAM_COLOR_WHITE.name} Team`,
                        players: [],
                        primaryColour: TOURNAMENT_TEAM_COLOR_WHITE.hex,
                    },
                ],
                bench: players.filter((p) => p.isActive !== false) || [],
            });
        }
    }, [players]);
    const { control, handleSubmit, setValue, formState: { errors }, } = useFormProps;
    const onSubmit = (data) => {
        mutation.mutate(data);
    };
    const loading = userTeamPermissionsIsLoading ||
        teamPreferencesIsLoading ||
        playersLoading ||
        mutation.isLoading;
    const error = userTeamPermissionsError || playersError || teamPreferencesError;
    if (loading) {
        return _jsx(Loading, {});
    }
    if (error) {
        return _jsx(ErrorView, { error: error });
    }
    if (!canModify) {
        return _jsx(Forbidden, {});
    }
    if (!teamPreference || !team) {
        return _jsx(ErrorView, { error: new Error("Unable to load data!") });
    }
    return (_jsxs(FormProvider, { ...useFormProps, children: [_jsx(ErrorSnackbar, { open: !!mutation.error, message: `Failed to Create tournament! Please try again.` }), _jsxs(Box, { component: "form", onSubmit: handleSubmit(onSubmit), noValidate: true, role: "form", sx: { mt: 1 }, children: [_jsxs(Typography, { variant: "h4", gutterBottom: true, sx: { fontSize: { xs: "1.75rem", sm: "2.125rem" } }, children: ["Create Intra-Squad Tournament for ", team.name] }), _jsxs(Box, { mt: 3, mb: 2, sx: { mb: 4 }, children: [_jsx(Typography, { variant: "h6", children: "Assign Players to Teams" }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "Assign each player to a team using the buttons on the bench. Remaining players stay on the bench." }), _jsx(Divider, { sx: { mt: 2 } })] }), _jsx(TeamChooser, { teamSelection: teamSelection, setTeamSelection: setTeamSelection, canEditTeamNames: true, minTeams: 3, maxTeams: 8 }), _jsx(Divider, { sx: { mt: 2 } }), _jsxs(Box, { mt: 4, mb: 2, children: [_jsx(Typography, { variant: "h6", children: "Set Match Rules & Details" }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "Define tournament structure, timing, and kick-off." }), _jsx(Divider, { sx: { mt: 2 } })] }), _jsx(Controller, { name: "tournamentName", control: control, defaultValue: "", rules: { required: "Tournament Name is required" }, render: ({ field }) => (_jsx(TextField, { ...field, margin: "normal", fullWidth: true, label: "Tournament Name", error: !!errors.tournamentName, helperText: errors.tournamentName?.message })) }), _jsx(Controller, { name: "startTime", control: control, rules: { required: "Start Time is required" }, render: ({ field }) => (_jsx(DateTimePicker, { label: "Start Time", ...field, value: field.value ? dayjs(field.value) : null, onChange: (newValue) => field.onChange(newValue), sx: { width: "100%" }, slotProps: {
                                textField: {
                                    error: !!errors.startTime,
                                    helperText: errors.startTime ? errors.startTime.message : "",
                                },
                            } })) }), _jsx(Controller, { control: control, name: "tournamentType", rules: { required: "Tournament type is required" }, render: ({ field }) => (_jsx(TextField, { ...field, required: true, select: true, fullWidth: true, margin: "normal", label: "Tournament Type", children: _jsx(MenuItem, { value: TournamentType.RoundRobin, children: "Round Robin" }) })) }), _jsx(Controller, { control: control, name: "halfLength", rules: {
                            required: "Half Length is required",
                            min: { value: 1, message: "Half Length must be at least 1" },
                            max: { value: 60, message: "Half Length must be 60 or less" },
                        }, render: ({ field }) => (_jsx(TextField, { ...field, select: true, fullWidth: true, margin: "normal", label: "Half Length (mins)", error: !!errors.halfLength, helperText: errors.halfLength?.message ||
                                "Length of a half. For single-half games, set the full game length here.", children: halfLengths.map((v) => (_jsx(MenuItem, { value: v, children: v }, v))) })) }), _jsx(Controller, { control: control, name: "halfTimeLength", rules: {
                            min: { value: 0, message: "Half-Time must be 0 or more" },
                            max: { value: 15, message: "Half-Time must be 15 or less" },
                        }, render: ({ field }) => (_jsx(TextField, { ...field, select: true, fullWidth: true, margin: "normal", label: "Half-Time Length (mins)", error: !!errors.halfTimeLength, helperText: errors.halfTimeLength?.message ||
                                "Length of the break between halves. Set to 0 for single-half games.", children: halfTimeLengths.map((v) => (_jsx(MenuItem, { value: v, children: v }, v))) })) }), _jsx(Controller, { control: control, name: "breakLength", rules: {
                            min: { value: 0, message: "Break must be 0 or more" },
                            max: { value: 60, message: "Break must be 60 or less" },
                        }, render: ({ field }) => (_jsx(TextField, { ...field, select: true, fullWidth: true, margin: "normal", label: "Break Length (mins)", error: !!errors.breakLength, helperText: errors.breakLength?.message || "Break time between matches.", children: breakLengths.map((v) => (_jsx(MenuItem, { value: v, children: v }, v))) })) }), _jsx(FormActions, { submitLabel: "Create", isSubmitting: mutation.isLoading })] })] }));
};
